import { useCallback, useMemo } from 'react'

import type { ResponsiveProps } from '@nivo/core'
import { LegendProps } from '@nivo/legends'
import {
  DefaultRawDatum,
  MayHaveLabel,
  PieCustomLayerProps,
  PieTooltipProps,
  ResponsivePie,
  type PieSvgProps,
} from '@nivo/pie'
import { Card } from 'antd'
import styled from 'styled-components'

import EmptyState from 'src/components/EmptyState'
import ChartTooltip from 'src/components/Visualizations/ChartTooltip'
import DashboardLabel from 'src/components/Visualizations/DashboardLabel'
import { ChartType } from 'src/components/Visualizations/util'
import Flex from 'src/govwell-ui/components/Flex/Flex'
import { useNivoChartFormatSettings } from 'src/hooks/use-nivo-chart-format-settings'

const PieChartCard = styled(Card)<{
  $flexBasis?: React.CSSProperties['flexBasis']
  $minWidth?: React.CSSProperties['minWidth']
}>`
  flex-basis: ${({ $flexBasis }) => $flexBasis ?? 'auto'};
  min-width: ${({ $minWidth }) => $minWidth ?? 'auto'};
  min-width: ${({ $minWidth }) => ($minWidth ? `max(34%, ${$minWidth})` : 'auto')};
`

// Long ids (e.g., code book item names) make the right-side legend column
// dominate the chart. Truncate the label rendered in the legend while keeping
// the original id intact so the hover tooltip still shows the full text.
const LEGEND_LABEL_MAX_CHARS = 70

// Once a pie chart has too many slices, the legend becomes unreadable and
// crowds the chart itself. Hide it past this threshold; the tooltip still
// surfaces per-slice labels on hover.
const LEGEND_MAX_ITEMS = 12

const truncateLegendLabel = (value: string): string =>
  value.length > LEGEND_LABEL_MAX_CHARS ? `${value.slice(0, LEGEND_LABEL_MAX_CHARS)}...` : value

type PieChartProps = ResponsiveProps<PieSvgProps<DefaultRawDatum>> & {
  label?: React.ReactNode
  labelTooltip?: string
  isLoading?: boolean
  formatValue?: (value?: number | null) => string
  innerRadius?: number
  padAngle?: number
  isCenterTextHidden?: boolean
}

export const PieChart = ({
  data,
  label,
  labelTooltip,
  isLoading,
  formatValue,
  innerRadius = 0.5,
  padAngle = 0.7,
  isCenterTextHidden = false,
  ...rest
}: PieChartProps) => {
  const total = data.reduce((sum, item) => sum + (item.value || 0), 0)

  // Data passed to <ResponsivePie>: keep `id` as the original full string so
  // the tooltip (which reads `datum.id`) still shows the full label, but add a
  // `label` field with the truncated text so Nivo's legend renders the
  // truncated version.
  const dataWithTruncatedLabels = useMemo<(DefaultRawDatum & MayHaveLabel)[]>(
    () =>
      data.map((datum) => ({
        ...datum,
        label: truncateLegendLabel(String(datum.id)),
      })),
    [data]
  )

  // Data passed to useNivoChartFormatSettings: substitute the truncated string
  // for `id` so the legend column width is sized to the truncated text rather
  // than the original (otherwise we over-reserve right-side space and shrink
  // the pie itself).
  const dataForFormatSettings = useMemo<DefaultRawDatum[]>(
    () =>
      data.map((datum) => ({
        ...datum,
        id: truncateLegendLabel(String(datum.id)),
      })),
    [data]
  )

  const hasLegend = data.length <= LEGEND_MAX_ITEMS

  const { margins, minWidthPx, flexBasisPx, isEmpty, theme, totalLegendWidthPx, legendWidthPx } =
    useNivoChartFormatSettings({
      chartType: ChartType.Pie,
      data: dataForFormatSettings,
      hasLegend,
    })

  const centerText = useCallback(
    (customLayerProps: PieCustomLayerProps<DefaultRawDatum>) => {
      return (
        <text
          x={customLayerProps.centerX}
          y={customLayerProps.centerY}
          textAnchor="middle"
          dominantBaseline="middle"
          className="text-2xl font-bold"
        >
          {total}
        </text>
      )
    },
    [total]
  )

  const legends: LegendProps[] = useMemo(() => {
    if (!totalLegendWidthPx || !legendWidthPx) {
      return []
    }

    return [
      {
        anchor: 'right',
        direction: 'column',
        justify: false,
        translateX: totalLegendWidthPx,
        translateY: 0,
        itemsSpacing: 6,
        itemDirection: 'left-to-right',
        itemWidth: legendWidthPx,
        itemHeight: 18,
        itemOpacity: 1,
        symbolSize: 11,
        symbolShape: 'circle',
      },
    ]
  }, [legendWidthPx, totalLegendWidthPx])

  const renderTooltip = useCallback((tooltipProps: PieTooltipProps<DefaultRawDatum>) => {
    const id = String(tooltipProps.datum.id)
    const tooltipValue = `${id}: ${tooltipProps.datum.formattedValue}`
    const tooltip = {
      id,
      value: tooltipValue,
    }
    return <ChartTooltip tooltips={[tooltip]} />
  }, [])

  const flexBasis = `${flexBasisPx}px`
  const minWidth = `${minWidthPx}px`

  return (
    <PieChartCard
      $flexBasis={flexBasis}
      $minWidth={minWidth}
      className="w-full flex-shrink flex-grow"
    >
      <Flex direction="column" gap={4}>
        <DashboardLabel label={label} tooltip={labelTooltip} isBold />
        <div className="h-[300px] w-full">
          {!isLoading && isEmpty ? (
            <EmptyState hideBorder>
              <EmptyState.Image />
              <EmptyState.Message>There is no data for the selected filters.</EmptyState.Message>
            </EmptyState>
          ) : (
            <ResponsivePie
              {...rest}
              data={dataWithTruncatedLabels}
              valueFormat={formatValue}
              margin={margins}
              innerRadius={innerRadius}
              padAngle={padAngle}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              borderWidth={1}
              borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
              legends={legends}
              layers={['arcs', 'legends', ...(isCenterTextHidden ? [] : [centerText])]}
              theme={theme}
              tooltip={renderTooltip}
            />
          )}
        </div>
      </Flex>
    </PieChartCard>
  )
}
